import React, { useEffect, useState } from 'react';
import { useLanguage } from '~/Hooks';
import { List, Checkbox } from '@mui/material';
import { BORDER_COLOR } from '~/Constants/Colors.constants';
import ListItemSelect from '~/Screens/Events/EventPage/Components/ListItemSelect';
import { PopoverToModal, SearchTextField } from '~/Components';
import { useBookingGroups } from '~/Hooks/useBookingGroups';
import { IBookingGroup } from '~/Models/DTO/BookingGroup.model';
import { makeStyles } from '~/UIKit/Style';

const useStyles = makeStyles(() => ({
  content: {
    padding: '0 15px',
  },
  searchContainer: {
    margin: '0 -15px',
  },
  bookingGroupList: {
    borderTop: `1px solid ${BORDER_COLOR}`,
    margin: '0 -15px',
    padding: 0,
  },
  Checkbox: {
    marginLeft: 'auto',
  },
}));

const useBookingGroupSelectionModal = ({
  selectedBookingGroupIds,
  updateBookingGroupListProp,
  onClose,
  open,
}: {
  updateBookingGroupListProp: (uuids: string[]) => void;
  selectedBookingGroupIds: string[];
  onClose: () => void;
  open: boolean;
}) => {
  const { bookingGroupsList } = useBookingGroups();
  const { currentLanguage } = useLanguage();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBookingGroups, setSelectedBookingGroup] = useState<IBookingGroup[]>([]);

  const sortedUnselectedBookingGroups = bookingGroupsList
    .filter((bookingGroup: IBookingGroup) => !selectedBookingGroups.includes(bookingGroup))
    .sort((firstGroup, secondGroup) =>
      firstGroup.name.localeCompare(secondGroup.name, currentLanguage, { sensitivity: 'base' }),
    );

  const filteredBookingGroups = [
    ...selectedBookingGroups,
    ...sortedUnselectedBookingGroups,
  ].filter((group) => group?.name?.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleActionTaken = () => {
    updateBookingGroupsList();
    onClose();
  };

  const updateBookingGroupsList = async () => {
    updateBookingGroupListProp(selectedBookingGroups?.map((group) => group?.uuid));
  };

  const toggleBookingGroup = (group: IBookingGroup) => {
    const index = selectedBookingGroups.findIndex((g) => g.uuid === group?.uuid);
    if (index !== -1) {
      setSelectedBookingGroup((oldTrainers) =>
        oldTrainers.filter((oldGroup) => oldGroup !== group),
      );
    } else {
      setSelectedBookingGroup((oldGroups) => [...oldGroups, group]);
    }
  };

  useEffect(() => {
    if (open) {
      setSearchQuery('');
    }
  }, [open]);

  useEffect(() => {
    setSelectedBookingGroup(
      bookingGroupsList?.filter((group) => selectedBookingGroupIds?.includes(group?.uuid)),
    );
  }, [bookingGroupsList]);

  return {
    BookingGroupList: filteredBookingGroups,
    doSearch: setSearchQuery,
    handleActionTaken,
    selectedBookingGroups,
    toggleBookingGroup,
  };
};

interface BookingGroupSelectionModalProps {
  open: boolean;
  onClose: () => void;
  updateBookingGroupList: (uuids: string[]) => void;
  selectedBookingGroupIds: string[];
  anchorEl?: null | Element;
}

export const BookingGroupSelectionModal = ({
  open,
  onClose,
  updateBookingGroupList: updateBookingGroupListProp,
  selectedBookingGroupIds,
  anchorEl,
}: BookingGroupSelectionModalProps) => {
  const { classes } = useStyles();
  const { t } = useLanguage();

  const {
    doSearch,
    BookingGroupList,
    toggleBookingGroup,
    handleActionTaken,
    selectedBookingGroups,
  } = useBookingGroupSelectionModal({
    updateBookingGroupListProp,
    selectedBookingGroupIds,
    onClose,
    open,
  });

  return (
    <PopoverToModal
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      title={t('settings.bookingGroup')}
      actionName={t('app.checkin.select.done')}
      onActionTaken={handleActionTaken}
    >
      <div className={classes.content}>
        <div className={classes.searchContainer}>
          <SearchTextField
            id="searchTrainer"
            onChange={(e) => doSearch(e.target.value)}
            placeholder={t('app.bookingGroup.searchForBookingGroup')}
            withBackground
          />
        </div>
        <List className={classes.bookingGroupList} data-testid="ChangeBookingGroupList">
          {BookingGroupList.map((group) => (
            <ListItemSelect
              key={`bookingGroup_modal_item_${group.uuid}`}
              onClick={() => toggleBookingGroup(group)}
              name={group.name}
              testId={'ChangeBookingGroupListItem'}
              bookingGroup={group}
              selectedBookingGroups={selectedBookingGroups}
            >
              <Checkbox
                checked={selectedBookingGroups.includes(group)}
                color="primary"
                className={classes.Checkbox}
              />
            </ListItemSelect>
          ))}
        </List>
      </div>
    </PopoverToModal>
  );
};
